import styled, { css } from 'styled-components';

export const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #006633;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-bottom: 4px;
  border-bottom: 1px solid #eef2f7;
`;

export const FieldGroup = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const FormField = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $span }) =>
    $span &&
    css`
      grid-column: span ${$span};
      @media (max-width: 576px) {
        grid-column: span 1;
      }
    `}

  label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  input,
  select,
  textarea {
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: #0f172a;
    background: #ffffff;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
    }

    &:focus {
      border-color: #006633;
      box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
    }

    &::placeholder {
      color: #94a3b8;
    }

    &:disabled {
      background: #f8fafc;
      color: #94a3b8;
      cursor: not-allowed;
    }
  }

  input,
  select {
    height: 40px;
  }

  textarea {
    padding: 10px 12px;
    min-height: 84px;
    line-height: 1.5;
    resize: vertical;
  }

  .error {
    color: #b91c1c;
    font-size: 12px;
    font-weight: 500;
  }
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: #006633;
  color: #ffffff;
  border: 1px solid transparent;

  &:hover {
    background: #00451f;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const GhostButton = styled.button`
  ${buttonBase}
  background: #ffffff;
  color: #334155;
  border: 1px solid #e2e8f0;

  &:hover {
    background: #f8fafc;
    border-color: #006633;
    color: #006633;
  }
`;

/* Avatar uploader */

export const AvatarUploader = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 14px;
  align-items: stretch;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const AvatarPreview = styled.div<{ $hasImage?: boolean }>`
  width: 88px;
  height: 88px;
  border-radius: 14px;
  border: 1px dashed
    ${({ $hasImage }) => ($hasImage ? 'transparent' : '#cbd5e1')};
  background: ${({ $hasImage }) => ($hasImage ? '#ffffff' : '#fbfcfe')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #94a3b8;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 14px;
  }

  @media (max-width: 576px) {
    width: 100%;
    height: 120px;
  }
`;

export const UploadDropzone = styled.label<{ $hasFile?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px dashed ${({ $hasFile }) => ($hasFile ? '#bbf7d0' : '#cbd5e1')};
  background: ${({ $hasFile }) => ($hasFile ? '#ecfdf5' : '#fbfcfe')};
  color: #475569;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #006633;
    background: #f1faf3;
  }

  input[type='file'] {
    display: none;
  }

  .icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${({ $hasFile }) => ($hasFile ? '#dcfce7' : '#e2e8f0')};
    color: ${({ $hasFile }) => ($hasFile ? '#006633' : '#475569')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .title {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hint {
    font-size: 11.5px;
    color: #64748b;
    font-weight: 500;
  }
`;

/* Legacy export — kept just to avoid import breaks if anyone references it */
export const PreviewImage = styled.img`
  background: white;
  padding: 8px;
  border-radius: 8px;
  max-width: 100%;
`;
